import { useId, useRef, useState } from 'react';
import { AlertCircle, CheckCircle2, FileText, Loader2, Upload } from 'lucide-react';
import type { KybDocumentStatus, KybDocumentType } from '../../api/types';
import { Button } from '../ui/Button';

type KybDocumentUploadProps = {
  docType: KybDocumentType;
  label: string;
  status: KybDocumentStatus;
  disabled?: boolean;
  onUpload: (file: File) => Promise<void>;
};

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function KybDocumentUpload({ docType: _docType, label, status, disabled, onUpload }: KybDocumentUploadProps) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [justUploaded, setJustUploaded] = useState<{ file_name: string; uploaded_at: string } | null>(null);

  const uploaded = status.uploaded;
  const incomplete = status.incomplete;
  const displayName = justUploaded?.file_name ?? status.file_name;
  const displayAt = justUploaded?.uploaded_at ?? status.uploaded_at;

  const pickFile = () => {
    if (!uploading && !disabled) inputRef.current?.click();
  };

  const onFileChange = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      await onUpload(file);
      setJustUploaded({ file_name: file.name, uploaded_at: new Date().toISOString() });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  let stateLabel = 'Required — not uploaded yet';
  let stateClass = 'text-onyx-muted';
  if (uploading) {
    stateLabel = 'Uploading…';
    stateClass = 'text-onyx-gold';
  } else if (uploaded) {
    stateLabel = 'On file — submitted to compliance';
    stateClass = 'text-onyx-success';
  } else if (incomplete) {
    stateLabel = 'Previous upload was incomplete — please upload again';
    stateClass = 'text-onyx-danger';
  } else if (error) {
    stateLabel = error;
    stateClass = 'text-onyx-danger';
  }

  return (
    <li className="rounded-xl border border-onyx-border bg-onyx-elevated/50 p-4 space-y-3" data-doc-type={_docType}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-onyx-text">{label}</p>
          <p className={`text-xs mt-1 ${stateClass}`}>{stateLabel}</p>
        </div>
        {uploading ? (
          <Loader2 className="shrink-0 text-onyx-gold animate-spin" size={22} aria-hidden />
        ) : uploaded ? (
          <CheckCircle2 className="shrink-0 text-onyx-success" size={22} aria-hidden />
        ) : incomplete ? (
          <AlertCircle className="shrink-0 text-onyx-danger" size={22} aria-hidden />
        ) : (
          <FileText className="shrink-0 text-onyx-muted" size={22} aria-hidden />
        )}
      </div>

      {uploaded && displayName ? (
        <div className="rounded-lg border border-onyx-success/30 bg-onyx-success/10 px-3 py-2 text-xs space-y-1">
          <p className="font-medium text-onyx-success truncate" title={displayName}>
            {displayName}
          </p>
          {displayAt ? (
            <p className="text-onyx-muted">Uploaded {new Date(displayAt).toLocaleString()}</p>
          ) : null}
          {status.size_bytes ? <p className="text-onyx-muted">{formatBytes(status.size_bytes)}</p> : null}
        </div>
      ) : null}

      <input
        id={inputId}
        ref={inputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
        className="sr-only"
        disabled={disabled || uploading}
        onChange={(e) => void onFileChange(e.target.files?.[0])}
      />

      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="ghost" disabled={disabled || uploading} onClick={pickFile}>
          {uploading ? (
            <>
              <Loader2 size={16} className="animate-spin" aria-hidden />
              Uploading…
            </>
          ) : (
            <>
              <Upload size={16} aria-hidden />
              {uploaded ? 'Replace file' : 'Choose file'}
            </>
          )}
        </Button>
      </div>
    </li>
  );
}
